import {
  DEFAULT_EN_FONT_NAME,
  DEFAULT_FONT_NAME,
  DEFAULT_JA_FONT_NAME,
} from "@shared/common/node/const";
import type {
  IDocumentNode,
  IFontName,
  PartialNode,
  SceneNode,
} from "@shared/common/node/type";

export const getFontKey = (fontName: IFontName): string => {
  return `${fontName.family}-${fontName.style}`;
};

export const parseFontKey = (key: string): IFontName => {
  if (!key.includes("-")) {
    throw new Error(`font key is wrong: ${key}`);
  }
  const lastIndex = key.lastIndexOf("-");

  return {
    family: key.substring(0, lastIndex),
    style: key.substring(lastIndex + 1),
  };
};

export const getDocumentFontKeySet = (
  data: PartialNode<IDocumentNode>,
  langauge: string = "ko"
): Set<string> => {
  const result: Set<string> = new Set();
  if (langauge === "en") {
    result.add(`${DEFAULT_EN_FONT_NAME}-normal`);
  } else if (langauge === "ja") {
    result.add(`${DEFAULT_JA_FONT_NAME}-normal`);
  } else {
    result.add(`${DEFAULT_FONT_NAME}-normal`);
  }

  return data.children
    ? data.children.reduce((acc, child) => {
        getFontKeySet(child).forEach((font) => acc.add(font));
        return acc;
      }, result)
    : result;
};

export const getFontKeySet = (node: PartialNode<SceneNode>): Set<string> => {
  const fonts = new Set<string>();
  const formFonts = new Set<string>();

  if (!node.type) {
    return fonts;
  }

  // 노드를 추가할 때마다 Font를 사용하는지에 대한 여부를 체크해야 하기 때문에
  // default: 절을 사용하지 않고 일일히 case를 나열합니다.
  switch (node.type) {
    case "FRAME":
      node.children?.reduce((acc, child) => {
        getFontKeySet(child).forEach((font) => acc.add(font));
        return acc;
      }, fonts);
      break;

    case "TEXT":
    case "TIMER":
    case "SOCIAL_PROOF":
      if (node.fontName) {
        fonts.add(getFontKey(node.fontName));
        if (node.content?.includes("<strong>")) {
          fonts.add(
            getFontKey({ family: node.fontName.family, style: "bold" })
          );
        }
      }
      break;

    case "SWIPER_PAGINATION":
      if (node.style?.type === "FRACTION" && node.style.fontName) {
        fonts.add(getFontKey(node.style.fontName));
      }
      break;

    case "PRODUCT_GRID_LIST":
    case "PRODUCT_X_SCROLL_LIST":
    case "AUTOMATED_PRODUCT_LIST": {
      if (node.fontName) {
        fonts.add(getFontKey(node.fontName));
      }
      if (node.productTitleFontName) {
        fonts.add(getFontKey(node.productTitleFontName));
      }
      if (node.originalPriceFontName) {
        fonts.add(getFontKey(node.originalPriceFontName));
      }
      if (node.discountRatioFontName) {
        fonts.add(getFontKey(node.discountRatioFontName));
      }

      break;
    }

    case "SINGLE_PRODUCT":
    case "FORM":
      if (node.fontName) {
        fonts.add(getFontKey(node.fontName));
        formFonts.add(getFontKey(node.fontName));
      }
      break;

    case "FORM_INPUT":
      if (node.label?.includes("<strong>")) {
        formFonts.forEach((font) => {
          fonts.add(getFontKey({ family: font, style: "bold" }));
        });
      }
      break;

    case "FORM_PRIVACY_POLICY":
      if (
        node.label?.includes("<strong>") ||
        node.content?.includes("<strong>")
      ) {
        formFonts.forEach((font) => {
          fonts.add(getFontKey({ family: font, style: "bold" }));
        });
      }
      break;

    case "CARD_WRAPPER": {
      if (!node.card) {
        break;
      }

      if (node.card.titleFontName) {
        fonts.add(getFontKey(node.card.titleFontName));
      }

      if (node.card.descriptionFontName) {
        fonts.add(getFontKey(node.card.descriptionFontName));
      }

      break;
    }

    case "SECTION_GROUP": {
      if (node.navFontName) {
        fonts.add(getFontKey(node.navFontName));
      }
      break;
    }
    case "IMAGE":
    case "VIDEO":
    case "INSTAGRAM_GALLERY":
    case "SLOT":
    case "SWIPER_BUTTON_GROUP":
    case "SWIPER_CONTAINER":
    case "FORM_DIVIDER":
    case "FORM_CHOICE":
    case "FORM_IMAGE_CHOICE":
    case "FORM_TEXT":
    case "FORM_DROPDOWN":
    case "FORM_RATING_SCALE":
    case "FORM_TEXTAREA":
    case "FORM_HIDDEN_FIELD":
    case "FORM_IMAGE_VIDEO":
    case "FORM_NPS":
    case "FORM_SEAN_ELLIS":
    case "FORM_FILE_UPLOADER":
      break;
  }

  // "inherit"으로 시작하는 폰트는 제외
  [...fonts].forEach((font) => {
    if (font.startsWith("inherit")) {
      fonts.delete(font);
    }
  });

  return fonts;
};

export const simpleRandomId = () => {
  const arr = window.crypto.getRandomValues(new Uint32Array(2));
  return (arr[0] as number).toString(36) + (arr[1] as number).toString(36);
};
